import { trackEvent } from 'user-analytics';

type CheckoutAccountEnrollmentMode = 'signup' | 'login';
type CheckoutAccountEnrollmentAuthMethod = 'email' | 'whatsapp';
type CheckoutAccountEnrollmentRequestType = 'initial' | 'retry' | 'resend';
type CheckoutAccountEnrollmentFailureStage = 'otp_request' | 'otp_validation' | 'session_refresh';
type AccountEnrollmentPlacement = 'Checkout' | 'Purchase Complete';

const CHECKOUT_ACCOUNT_ENROLLMENT_EVENTS = Object.freeze({
  STARTED: 'Account Enrollment Started',
  OTP_SENT: 'Account Enrollment OTP Sent',
  OTP_SUBMITTED: 'Account Enrollment OTP Submitted',
  SUCCEEDED: 'Account Enrollment Succeeded',
  FAILED: 'Account Enrollment Failed',
  DISMISSED: 'Account Enrollment Dismissed',
  INVITATION_VIEWED: 'Account Enrollment Invitation Viewed',
});

const AUTH_SYSTEM = 'commerce_api';
const METHOD_LABELS: Record<CheckoutAccountEnrollmentAuthMethod, string> = {
  email: 'Email OTP',
  whatsapp: 'WhatsApp',
};

interface RequestEventProperties {
  mode: CheckoutAccountEnrollmentMode;
  authMethod: CheckoutAccountEnrollmentAuthMethod;
  requestType: CheckoutAccountEnrollmentRequestType;
  placement?: AccountEnrollmentPlacement;
}

interface FailureEventProperties {
  mode: CheckoutAccountEnrollmentMode;
  authMethod: CheckoutAccountEnrollmentAuthMethod;
  stage: CheckoutAccountEnrollmentFailureStage;
  errorCode?: string;
  requestType?: CheckoutAccountEnrollmentRequestType;
  placement?: AccountEnrollmentPlacement;
}

interface AuthenticationEventProperties {
  mode: CheckoutAccountEnrollmentMode;
  authMethod: CheckoutAccountEnrollmentAuthMethod;
  placement?: AccountEnrollmentPlacement;
}

function commonProperties({
  mode,
  authMethod,
  placement = 'Checkout',
}: AuthenticationEventProperties) {
  return {
    'Enrollment Mode': mode,
    'Method': METHOD_LABELS[authMethod],
    'Auth System': AUTH_SYSTEM,
    'Placement': placement,
  };
}

function requestProperties({ mode, authMethod, requestType, placement }: RequestEventProperties) {
  return {
    ...commonProperties({ mode, authMethod, placement }),
    'Request Type': requestType,
  };
}

function trackCheckoutAccountEnrollmentStarted(properties: RequestEventProperties): void {
  trackEvent(CHECKOUT_ACCOUNT_ENROLLMENT_EVENTS.STARTED, requestProperties(properties));
}

function trackCheckoutAccountEnrollmentOtpSent(properties: RequestEventProperties): void {
  trackEvent(CHECKOUT_ACCOUNT_ENROLLMENT_EVENTS.OTP_SENT, requestProperties(properties));
}

function trackCheckoutAccountEnrollmentOtpSubmitted({
  mode,
  authMethod,
  placement,
}: AuthenticationEventProperties): void {
  trackEvent(
    CHECKOUT_ACCOUNT_ENROLLMENT_EVENTS.OTP_SUBMITTED,
    commonProperties({ mode, authMethod, placement }),
  );
}

function trackCheckoutAccountEnrollmentSucceeded({
  mode,
  authMethod,
  placement,
}: AuthenticationEventProperties): void {
  trackEvent(
    CHECKOUT_ACCOUNT_ENROLLMENT_EVENTS.SUCCEEDED,
    commonProperties({ mode, authMethod, placement }),
  );
}

function trackCheckoutAccountEnrollmentFailed({
  mode,
  authMethod,
  stage,
  errorCode = 'UNKNOWN',
  requestType,
  placement,
}: FailureEventProperties): void {
  trackEvent(CHECKOUT_ACCOUNT_ENROLLMENT_EVENTS.FAILED, {
    ...commonProperties({ mode, authMethod, placement }),
    'Failure Stage': stage,
    'Error Code': errorCode,
    ...(requestType && { 'Request Type': requestType }),
  });
}

function trackCheckoutAccountEnrollmentDismissed({
  mode,
  authMethod,
  placement,
}: {
  mode: CheckoutAccountEnrollmentMode | 'pending';
  authMethod: CheckoutAccountEnrollmentAuthMethod;
  placement?: AccountEnrollmentPlacement;
}): void {
  trackEvent(CHECKOUT_ACCOUNT_ENROLLMENT_EVENTS.DISMISSED, {
    'Enrollment Mode': mode,
    'Method': METHOD_LABELS[authMethod],
    'Auth System': AUTH_SYSTEM,
    'Placement': placement || 'Checkout',
  });
}

function trackAccountEnrollmentInvitationViewed(properties: AuthenticationEventProperties): void {
  trackEvent(CHECKOUT_ACCOUNT_ENROLLMENT_EVENTS.INVITATION_VIEWED, commonProperties(properties));
}

export {
  CHECKOUT_ACCOUNT_ENROLLMENT_EVENTS,
  trackCheckoutAccountEnrollmentDismissed,
  trackCheckoutAccountEnrollmentFailed,
  trackCheckoutAccountEnrollmentOtpSent,
  trackCheckoutAccountEnrollmentOtpSubmitted,
  trackCheckoutAccountEnrollmentStarted,
  trackCheckoutAccountEnrollmentSucceeded,
  trackAccountEnrollmentInvitationViewed,
};
export type {
  CheckoutAccountEnrollmentFailureStage,
  CheckoutAccountEnrollmentAuthMethod,
  CheckoutAccountEnrollmentMode,
  CheckoutAccountEnrollmentRequestType,
  AccountEnrollmentPlacement,
};
